import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  HostListener,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { Chart, ChartConfiguration, ChartDataset } from "chart.js";
import { IAlertResponseTime, IEmaacResponseTimes } from "app/models/statistics.model";
import { RESPONSE_TIME_HISTOGRAM_CHART_CONFIGS } from "app/shared/chart-configs.shared";
import { StatisticsExportCaptureService } from "app/services/statistics-export-capture.service";
import {
  StatisticsExportSnapshot,
  StatisticsExportWidget,
} from "app/services/statistics-export.models";
import { StatisticsPngDownloadBtnComponent } from "../statistics-png-download-btn/statistics-png-download-btn.component";

const CHART_EXPORT_HEIGHT_PX = 340;
const CHART_EXPORT_WIDTH_PX = 900;

/**
 * Maximum number of 1-second bins. Caps memory and canvas size; any response
 * time beyond this folds into the final bin (10 minutes = 600 s).
 */
const MAX_HISTOGRAM_BINS = 600;

/** Time label: seconds < 60 shown as "Xs", otherwise "M:SS" or "M min". */
function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem === 0 ? `${m} min` : `${m}:${String(rem).padStart(2, "0")}`;
}

interface LineData {
  labels: string[];
  datasets: ChartDataset<"line">[];
}

const LINE_OPTIONS: Partial<ChartConfiguration<"line">["options"]> = {
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#707070", font: { size: 12 }, maxRotation: 45 },
    },
    y: {
      beginAtZero: true,
      grid: { display: true, drawBorder: false } as any,
      ticks: { display: true, precision: 0, color: "#707070" },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
  },
};

@Component({
  selector: "response-time-histogram",
  templateUrl: "./response-time-histogram.component.html",
  styleUrls: ["./response-time-histogram.component.css"],
  standalone: true,
  imports: [TranslateModule, StatisticsPngDownloadBtnComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponseTimeHistogramComponent
  implements OnChanges, OnDestroy, StatisticsExportWidget
{
  constructor(private captureService: StatisticsExportCaptureService) {}

  @Input() responseTimes: IEmaacResponseTimes;
  @ViewChild('chartCanvas', { static: true }) canvasRef: ElementRef<HTMLCanvasElement>;

  chart: Chart;

  @HostListener("contextmenu", ["$event"])
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["responseTimes"]) {
      this.drawChart();
    }
  }

  /**
   * Build 1-second bins from min to max response time, combined across all device
   * types. Bins with zero count are removed so the interpolated line never dips to
   * zero between real data points.
   */
  private buildLineData(responses: IAlertResponseTime[]): LineData {
    if (!responses?.length) return { labels: [], datasets: [] };

    // Clamp/filter non-finite values and negatives so they cannot produce NaN indices.
    const valid = responses
      .map((r) => (Number.isFinite(r.responseTime) ? Math.max(0, Math.round(r.responseTime)) : 0));

    // Cap outliers into the final open-ended bin (index MAX_HISTOGRAM_BINS, i.e. 600 s)
    // so one extreme value cannot make the chart huge. Track whether anything actually
    // exceeded the cap so the bin is only labelled open-ended ("≥ 10 min") when real
    // outliers were folded in — an exact 600 s value with no outliers stays "10 min".
    const maxBin = MAX_HISTOGRAM_BINS;
    const counts = new Array<number>(MAX_HISTOGRAM_BINS + 1).fill(0);
    let hasOverflow = false;
    for (const t of valid) {
      if (t > maxBin) hasOverflow = true;
      counts[Math.min(t, maxBin)]++;
    }

    // Keep only bins that actually have responses (remove leading, trailing and
    // interior zeros so the line is fully interpolated without artificial dips).
    const nonZeroIndices = counts
      .map((c, i) => (c > 0 ? i : -1))
      .filter((i) => i >= 0);

    if (!nonZeroIndices.length) return { labels: [], datasets: [] };

    const labels = nonZeroIndices.map((i) =>
      i >= maxBin && hasOverflow ? `≥ ${formatSeconds(maxBin)}` : formatSeconds(i)
    );
    const data = nonZeroIndices.map((i) => counts[i]);

    const datasets: ChartDataset<"line">[] = [{
      data,
      borderColor: "rgb(0, 136, 227)",
      backgroundColor: "rgba(0, 136, 227, 0.12)",
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.4,
      fill: true,
    }];

    return { labels, datasets };
  }

  private buildChartConfig(
    labels: string[],
    datasets: ChartDataset<"line">[]
  ): ChartConfiguration<"line"> {
    return {
      type: "line",
      data: { datasets, labels },
      options: {
        ...RESPONSE_TIME_HISTOGRAM_CHART_CONFIGS.options,
        ...LINE_OPTIONS,
      },
    };
  }

  drawChart(): void {
    this.chart?.destroy();
    const { labels, datasets } = this.buildLineData(
      this.responseTimes?.alertResponses ?? []
    );
    this.chart = new Chart(this.canvasRef.nativeElement, this.buildChartConfig(labels, datasets));
  }

  async captureForExport(): Promise<StatisticsExportSnapshot | null> {
    const responses = this.responseTimes?.alertResponses;
    if (!responses?.length) return null;

    const { labels, datasets } = this.buildLineData(responses);

    return this.captureService.buildOffscreenChartSnapshot(
      this.buildChartConfig(labels, datasets),
      "response-time-histogram",
      "RESPONSE_TIME_DISTRIBUTION",
      {
        chartExportWidthPx: CHART_EXPORT_WIDTH_PX,
        chartExportHeightPx: CHART_EXPORT_HEIGHT_PX,
        chartDevicePixelRatio: 2,
      }
    );
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
}
